/**
 * Role-based task views — the single source of truth for "which screen does
 * this account get for this task, and what may it touch there?".
 *
 * Three answers only, and the UI asks for them here rather than testing roles
 * of its own (`src/modules/tasks/TaskDetailRouter.tsx` is a thin renderer over
 * {@link resolveTaskView}):
 *
 *  1. `manager` — a super-admin, or a manager holding `EDIT_TASK` / `ASSIGN_TASK`
 *     (or the unit-wide `VIEW_ALL_TASKS`, read-only). The whole task: internal
 *     manager notes, every vehicle team, the timeline and the closure button.
 *  2. `soldier`  — a soldier bound to one of the task's vehicle teams. Their own
 *     vehicle, their own team slot and their own field actions. Nothing else on
 *     the task is reachable from that view.
 *  3. `denied`   — anyone else, including a soldier the task was never assigned
 *     to: "אינך משויך למשימה זו".
 *
 * The permission answers themselves still come from `src/core/permissions.ts`;
 * this module only decides which question to ask.
 */
import {
  canActOnTask,
  canViewInternalNotes,
  hasAnyPermission,
  hasPermission,
} from './permissions';
import { teamSoldierIds } from './assignment-rules';
import type { CrewRole, TaskCrewMember, TaskTeam } from '../types/assignment';
import type { Soldier } from '../types/fleet';
import type { TaskScope, User, UserPermission } from '../types/user';

/** Holding either of these is what makes an account a manager of a task. */
export const MANAGER_VIEW_PERMISSIONS: readonly UserPermission[] = ['EDIT_TASK', 'ASSIGN_TASK'];

// ── The three answers ────────────────────────────────────────────────

export const TASK_VIEW_MODES = ['manager', 'soldier', 'denied'] as const;

export type TaskViewMode = (typeof TASK_VIEW_MODES)[number];

/** Why the door was closed — each one reads differently to the person at it. */
export const TASK_VIEW_DENIED_REASONS = [
  'NOT_ASSIGNED',
  'NO_SOLDIER_RECORD',
  'ACCOUNT_INACTIVE',
] as const;

export type TaskViewDeniedReason = (typeof TASK_VIEW_DENIED_REASONS)[number];

export const TASK_VIEW_DENIED_MESSAGES: Record<TaskViewDeniedReason, string> = {
  NOT_ASSIGNED: 'אינך משויך למשימה זו',
  NO_SOLDIER_RECORD: 'החשבון שלך אינו מקושר לרשומת חייל במצבת',
  ACCOUNT_INACTIVE: 'החשבון אינו פעיל או ממתין לאישור',
};

/** What the router hands the chosen view. */
export interface TaskViewResolution {
  mode: TaskViewMode;
  /** Set on `denied` only. */
  reason?: TaskViewDeniedReason;
  /** The Hebrew sentence the denied view shows. */
  message?: string;
  /** Set on `soldier` — the roster record behind the account. */
  soldierId?: string;
  /** Set on `soldier` — the one vehicle team they ride with. */
  team?: TaskTeam;
  /** Set on `soldier` — the slot they were placed in. */
  crewRole?: CrewRole;
  /** Manager capabilities, already answered by the permissions engine. */
  canEdit: boolean;
  canAssign: boolean;
  canClose: boolean;
  canViewInternalNotes: boolean;
  /** Soldier capability: may report receipt, updates and execution finished. */
  canReportFromField: boolean;
}

export interface TaskViewContext {
  /** Every vehicle team on the task, crew included. */
  teams: readonly TaskTeam[];
  /** The roster, for resolving the account behind a crew member. */
  soldiers: readonly Soldier[];
}

// ── Roster ↔ account ─────────────────────────────────────────────────

/**
 * The roster record behind an account. The link is `Soldier.userId`; the
 * personal number is only consulted when the record was never linked, which is
 * how a soldier added to the מצבת before their account existed still resolves.
 */
export function soldierRecordFor(
  user: User | null | undefined,
  soldiers: readonly Soldier[],
): Soldier | null {
  if (!user) return null;
  const linked = soldiers.find((soldier) => soldier.userId === user.id);
  if (linked) return linked;
  if (!user.personalNumber) return null;
  return soldiers.find((soldier) => soldier.personalNumber === user.personalNumber) ?? null;
}

/** Every roster soldier bound to the task, in any role and on any vehicle. */
export function assignedSoldierIdsOf(teams: readonly TaskTeam[]): string[] {
  return [...new Set(teams.flatMap((team) => teamSoldierIds(team)))];
}

/**
 * The task as the permissions engine reads it: the accounts behind the crew.
 * A crew member with no account, and every external passenger, simply holds no
 * user id — so neither can widen anyone's access.
 */
export function taskScopeFor(
  teams: readonly TaskTeam[],
  soldiers: readonly Soldier[],
): TaskScope {
  const assigned = new Set(assignedSoldierIdsOf(teams));
  const assignedUserIds = soldiers
    .filter((soldier) => assigned.has(soldier.id) && Boolean(soldier.userId))
    .map((soldier) => soldier.userId as string);
  return { assignedUserIds: [...new Set(assignedUserIds)] };
}

// ── The soldier's slice of the task ──────────────────────────────────

/** The one vehicle team a soldier rides with on this task. */
export function soldierTeamOf(
  teams: readonly TaskTeam[],
  soldierId: string | null | undefined,
): TaskTeam | null {
  if (!soldierId) return null;
  return teams.find((team) => teamSoldierIds(team).includes(soldierId)) ?? null;
}

/** The slot the soldier was placed in on their own team. */
export function crewRoleOf(
  team: TaskTeam | null | undefined,
  soldierId: string | null | undefined,
): CrewRole | null {
  if (!team || !soldierId) return null;
  if (team.driverId === soldierId) return 'driver';
  if (team.escortOption === 'soldier' && team.escortId === soldierId) return 'escort';
  return team.passengers.some((passenger) => passenger.soldierId === soldierId)
    ? 'passenger'
    : null;
}

/** The passengers riding with the soldier — themselves excluded. */
export function crewMatesOf(
  team: TaskTeam | null | undefined,
  soldierId: string | null | undefined,
): TaskCrewMember[] {
  if (!team) return [];
  return team.passengers.filter((passenger) => passenger.soldierId !== soldierId);
}

/**
 * The vehicle teams belonging to one task, out of a board-wide load.
 *
 * The board loads every convoy on screen in one round-trip and then needs each
 * task's own slice of it. That split lives here rather than on the screen, so
 * the tasks UI keeps no selection rule of its own — it is the same boundary
 * {@link visibleTeamsFor} then narrows down to what the viewer may read.
 */
export function teamsOfTask(teams: readonly TaskTeam[], taskId: string): TaskTeam[] {
  return teams.filter((team) => team.taskId === taskId);
}

/**
 * The vehicle teams of a task this account may actually read.
 *
 * A manager reads the whole convoy; a soldier reads the one vehicle they ride
 * with, and nothing else — the same boundary {@link resolveTaskView} draws for
 * the detail screens, applied wherever a task's crew is rendered outside them.
 * The board's shareable dispatch order goes through here, so a soldier can
 * never share a convoy they were never shown.
 */
export function visibleTeamsFor(
  user: User | null | undefined,
  context: TaskViewContext,
): TaskTeam[] {
  const teams = context.teams ?? [];
  if (isManagerViewer(user)) return [...teams];
  const soldier = soldierRecordFor(user, context.soldiers ?? []);
  const team = soldierTeamOf(teams, soldier?.id);
  return team ? [team] : [];
}

// ── The router's answer ──────────────────────────────────────────────

/** A manager-side viewer: a super-admin, or an account that may act on tasks. */
export function isManagerViewer(user: User | null | undefined): boolean {
  if (!user || user.role === 'soldier') return false;
  return (
    hasAnyPermission(user, MANAGER_VIEW_PERMISSIONS) || canViewInternalNotes(user)
  );
}

const denied = (reason: TaskViewDeniedReason): TaskViewResolution => ({
  mode: 'denied',
  reason,
  message: TASK_VIEW_DENIED_MESSAGES[reason],
  canEdit: false,
  canAssign: false,
  canClose: false,
  canViewInternalNotes: false,
  canReportFromField: false,
});

/**
 * Which view this account gets for this task. The order is the security
 * boundary itself: manager rights are checked first, then crew membership, and
 * anything that reaches the end is refused.
 */
export function resolveTaskView(
  user: User | null | undefined,
  context: TaskViewContext,
): TaskViewResolution {
  const teams = context.teams ?? [];
  const soldiers = context.soldiers ?? [];

  if (isManagerViewer(user)) {
    return {
      mode: 'manager',
      canEdit: hasPermission(user, 'EDIT_TASK'),
      canAssign: hasPermission(user, 'ASSIGN_TASK'),
      // Closing a task is an edit of the task itself, never a field report.
      canClose: hasPermission(user, 'EDIT_TASK'),
      canViewInternalNotes: canViewInternalNotes(user),
      canReportFromField: false,
    };
  }

  if (!user || !hasPermission(user, 'VIEW_TASKS')) return denied('ACCOUNT_INACTIVE');

  const soldier = soldierRecordFor(user, soldiers);
  if (!soldier) return denied('NO_SOLDIER_RECORD');

  const team = soldierTeamOf(teams, soldier.id);
  const crewRole = crewRoleOf(team, soldier.id);
  if (!team || !crewRole) return denied('NOT_ASSIGNED');

  // The engine has the last word: a soldier acts only on their own tasks. The
  // account is added to the scope because its roster record was just found on
  // the crew — that is the same fact `taskScopeFor()` reads off `Soldier.userId`,
  // and it also covers a record that was matched by personal number.
  const base = taskScopeFor(teams, soldiers).assignedUserIds;
  const scope: TaskScope = { assignedUserIds: [...new Set([...base, user.id])] };
  if (!canActOnTask(user, 'VIEW_TASKS', scope)) return denied('NOT_ASSIGNED');

  return {
    mode: 'soldier',
    soldierId: soldier.id,
    team,
    crewRole,
    canEdit: false,
    canAssign: false,
    canClose: false,
    canViewInternalNotes: false,
    canReportFromField: canActOnTask(user, 'UPDATE_TASK_STATUS', scope),
  };
}
